import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  CheckCircle2, Circle, Clock, ChevronRight, Sparkles, Camera, MapPin,
  Calendar, Stethoscope, BookOpen, ArrowRight, X, Loader2, PartyPopper,
  ExternalLink, Copy, Check, Printer,
} from 'lucide-react';
import api from '@/lib/api';

/**
 * NewDoctorOnboardingWizard — guided "Start here" experience for the
 * Free / Gold tier.
 *
 *  Why this exists
 *  ---------------
 *  A first-time doctor lands on a 13-tab dashboard with no idea where to
 *  start. The existing SetupBlocker tells them WHAT is missing but not
 *  WHAT TO DO FIRST + HOW MUCH TIME + WHAT DATA TO HAVE READY. This
 *  component groups the existing 11 publishability checks into 5 logical
 *  STEPS, surfaces one step at a time as the active focus, and tells the
 *  doctor exactly what to keep handy before tapping the CTA.
 *
 *  When does it render?
 *  --------------------
 *    - Tier is `free` (or unset / 'gold').
 *    - The /dashboard/score endpoint reports `is_publishable === false`.
 *  Premium and Turbo doctors keep the original dashboard layout. Once a
 *  Free doctor finishes all 5 steps, this component auto-collapses into
 *  a green "🎉 Your profile is live!" celebratory card with a one-tap
 *  WhatsApp share. Future logins land them straight on the full dashboard.
 *
 *  Wires into the parent `<DoctorDashboard>` via:
 *    - `onSetActiveTab(tabKey)` — switch tabs when a step's CTA is tapped.
 *    - `onWizardActive(bool)`   — tell the parent to hide its other tabs
 *                                  while the wizard is the focus.
 */
const FIVE_STEPS = [
  {
    key: 'profile_basics',
    label: 'Tell patients about you',
    icon: Camera,
    time_min: 3,
    tab: 'profile',
    cta: 'Open Profile tab',
    keep_ready: [
      'A sharp portrait photo (selfie or studio — square frame works best)',
      'Your NMC / state registration number',
      'A 1-line tagline (e.g. "Cardiologist with 12 years\' bypass experience")',
      'A 2-3 line bio in your own voice',
      'Your specialization(s) — pick from the list',
    ],
    check_keys: ['photo', 'bio', 'qualifications', 'registration', 'specializations'],
    summary_done: 'Photo, bio, qualifications, registration number and specialty all set.',
  },
  {
    key: 'add_clinic',
    label: 'Add your clinic location',
    icon: MapPin,
    time_min: 1,
    tab: 'locations',
    cta: 'Add my clinic',
    keep_ready: [
      'Your clinic / hospital name as it shows on Google Maps',
      'Or just the address — pincode + city is enough to start',
    ],
    helper: 'Type the name and pick from the dropdown — the address, pincode and map pin auto-fill in one tap.',
    check_keys: ['location'],
    summary_done: 'At least one clinic is on your profile with a verified address.',
  },
  {
    key: 'set_schedule',
    label: 'Set your OPD hours',
    icon: Calendar,
    time_min: 2,
    tab: 'schedule',
    cta: 'Set my hours',
    keep_ready: [
      'Which days of the week you see patients',
      'Morning + evening time slots per clinic',
      'Approximate slot duration (e.g. 15 min per patient)',
    ],
    check_keys: ['schedule'],
    summary_done: 'Weekly schedule saved — patients can see your free slots.',
  },
  {
    key: 'pick_procedures',
    label: 'Pick your top procedures + prices',
    icon: Stethoscope,
    time_min: 2,
    tab: 'procedures',
    cta: 'Auto-fill catalog',
    keep_ready: [
      'Your top 5 procedures / consultations',
      'A rough price range for each (e.g. ₹500 – ₹800 OPD consult)',
    ],
    helper: 'Tap "Auto-fill from catalog" — 30+ ready procedures load in one click. Tweak prices on only the ones you actually offer.',
    check_keys: ['procedures', 'procedure_price'],
    summary_done: 'At least 3 procedures listed with pricing.',
  },
  {
    key: 'authored_voice',
    label: 'Make it sound like you',
    icon: BookOpen,
    time_min: 5,
    tab: 'profile',
    cta: 'Write my bio',
    keep_ready: [
      '5 minutes to hand-write your bio in your own clinical voice',
      'Personal anecdotes / your way of explaining things in 1-2 lines',
      'Coffee Premium doctors: 3 lifetime AI pulls available to seed bio/procedures/FAQ',
    ],
    helper: 'Free tier hand-writes every section (no AI templates) — Google rewards authentic voice. Coffee Premium adds a one-time AI on-ramp; TurboCharge adds monthly AI drafts. ≥40% authentic voice is the bar in every tier.',
    check_keys: ['voice_authenticity'],
    summary_done: 'Your authored voice meets the SEO threshold.',
  },
];

function stepStatus(step, checks) {
  const map = Object.fromEntries((checks || []).map(c => [c.key, c.done]));
  const required = step.check_keys.map(k => map[k]);
  if (required.every(Boolean)) return 'done';
  if (required.some(Boolean)) return 'partial';
  return 'todo';
}


export default function NewDoctorOnboardingWizard({ tier, onSetActiveTab, onWizardActive, slug }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);  // which step's "keep ready" panel is open
  const [skipped, setSkipped] = useState(() => sessionStorage.getItem('wizardSkipped') === '1');
  const [copied, setCopied] = useState(false);
  const wasIncompleteRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/dashboard/score');
      setData(r.data);
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Re-poll on focus so the wizard reflects progress when the doctor
  // bounces between tabs.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const isFreeTier = !tier || tier === 'free' || tier === 'gold';
  const isPublishable = data?.is_publishable === true;
  const active = isFreeTier && !isPublishable && !skipped && data !== null;
  const showCelebration = isFreeTier && isPublishable && !skipped && wasIncompleteRef.current === true;

  // Tell parent so it can hide the dashboard tab bar while the wizard is
  // the focus.
  useEffect(() => {
    onWizardActive?.(active);
  }, [active, onWizardActive]);

  // Track was-incomplete so we know when to fire the celebration.
  useEffect(() => {
    if (!data) return;
    if (wasIncompleteRef.current === null) {
      wasIncompleteRef.current = !data.is_publishable;
    } else if (!data.is_publishable) {
      wasIncompleteRef.current = true;
    }
  }, [data]);

  if (!data) return null;
  if (!isFreeTier) return null;
  if (skipped && !showCelebration) return null;

  const checks = data.checks || [];
  const stepsWithStatus = FIVE_STEPS.map(s => ({ ...s, status: stepStatus(s, checks) }));
  const doneCount = stepsWithStatus.filter(s => s.status === 'done').length;
  const currentStep = stepsWithStatus.find(s => s.status !== 'done') || stepsWithStatus[stepsWithStatus.length - 1];
  const totalTime = FIVE_STEPS.reduce((sum, s) => sum + s.time_min, 0);
  const timeLeft = stepsWithStatus.filter(s => s.status !== 'done').reduce((sum, s) => sum + s.time_min, 0);

  // ─── Celebration card after profile goes live ─────────────────────
  if (showCelebration) {
    const profileUrl = slug ? `${window.location.origin}/dr/${slug}` : '';
    const shareMsg = `Hi! I've just launched my doctor website — quick way to see availability + book an appointment with me: ${profileUrl}`;
    const onCopy = () => {
      navigator.clipboard?.writeText(profileUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    const wa = `https://wa.me/?text=${encodeURIComponent(shareMsg)}`;
    return (
      <div className="rounded-3xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-sky-50 p-6 md:p-8 mb-6 shadow-sm" data-testid="onboarding-celebration">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
            <PartyPopper size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-widest">You're Live</p>
            <h3 className="text-xl md:text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your profile is now public + indexable on Google.
            </h3>
            <p className="text-sm text-slate-600 mt-1.5">
              Share your link in your patient WhatsApp groups — every share helps Google rank you faster.
            </p>
            {profileUrl && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <code className="text-xs bg-white border border-slate-200 px-3 py-2 rounded-lg text-slate-700 max-w-full truncate" data-testid="celebration-url">{profileUrl}</code>
                <button onClick={onCopy} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200" data-testid="celebration-copy">
                  {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy link</>}
                </button>
                <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700" data-testid="celebration-whatsapp">
                  Share on WhatsApp <ArrowRight size={12} />
                </a>
                <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-white border border-slate-200 hover:bg-sky-50 text-sky-700" data-testid="celebration-view">
                  View my website <ExternalLink size={12} />
                </a>
              </div>
            )}
            <button onClick={() => { sessionStorage.setItem('wizardSkipped', '1'); setSkipped(true); }}
              className="mt-4 text-xs text-slate-500 underline hover:text-slate-700"
              data-testid="celebration-dismiss">
              Continue to dashboard →
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!active) return null;

  // ─── Active wizard ─────────────────────────────────────────────────
  return (
    <div className="rounded-3xl border-2 border-sky-200 bg-gradient-to-br from-sky-50/60 via-white to-indigo-50/40 p-6 md:p-8 mb-6 shadow-sm" data-testid="onboarding-wizard">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold text-sky-700 uppercase tracking-widest">Start here · Gold plan</p>
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Go live on Google in 5 quick steps
          </h2>
          <p className="text-sm text-slate-600 mt-1.5">
            About <strong>{totalTime} minutes total</strong> · {timeLeft} min left ·
            {' '}<span className="text-emerald-600 font-semibold">{doneCount} of {FIVE_STEPS.length} done</span>
          </p>
        </div>
        <button onClick={() => { sessionStorage.setItem('wizardSkipped', '1'); setSkipped(true); }}
          className="text-xs text-slate-500 hover:text-slate-700 underline shrink-0"
          data-testid="wizard-skip">
          Skip for now
        </button>
      </div>

      {/* Two complementary references — a tappable visual walkthrough
          (best on phone) and a printable lean 2-pager. */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <a
          href="/visual-walkthrough"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 hover:text-sky-900 underline-offset-2 hover:underline"
          data-testid="wizard-visual-walkthrough"
        >
          <Camera size={12} /> Open visual walkthrough (with screenshots)
        </a>
        <a
          href="/onboarding-guide"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 hover:text-sky-900 underline-offset-2 hover:underline"
          data-testid="wizard-print-guide"
        >
          <Printer size={12} /> Print 2-page reference sheet
        </a>
      </div>

      {/* Progress bar */}
      <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-all duration-500"
          style={{ width: `${(doneCount / FIVE_STEPS.length) * 100}%` }}
          data-testid="wizard-progress" />
      </div>

      {/* 5-step list */}
      <ol className="mt-5 space-y-2" data-testid="wizard-steps">
        {stepsWithStatus.map((step, idx) => {
          const isCurrent = step.key === currentStep.key && step.status !== 'done';
          const isDone = step.status === 'done';
          const isExpanded = expanded === step.key || isCurrent;
          const Icon = step.icon;
          return (
            <li key={step.key} className={`rounded-2xl border transition-colors ${isDone ? 'border-emerald-200 bg-emerald-50/30' : isCurrent ? 'border-sky-300 bg-white shadow-sm ring-2 ring-sky-100' : 'border-slate-200 bg-white'}`}
              data-testid={`wizard-step-${step.key}`}>
              <button
                type="button"
                onClick={() => setExpanded(expanded === step.key ? null : step.key)}
                className="w-full text-left flex items-center gap-3 px-4 py-3"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isDone ? 'bg-emerald-500 text-white' : isCurrent ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                  {isDone ? <CheckCircle2 size={18} /> : <Icon size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${isDone ? 'text-emerald-700 line-through decoration-emerald-300' : 'text-slate-900'}`}>
                    Step {idx + 1}: {step.label}
                  </p>
                  {!isDone && (
                    <p className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1">
                      <Clock size={11} /> ~{step.time_min} min
                    </p>
                  )}
                  {isDone && (
                    <p className="text-xs text-emerald-600 mt-0.5">{step.summary_done}</p>
                  )}
                </div>
                {!isDone && (
                  <ChevronRight size={16} className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                )}
              </button>

              {isExpanded && !isDone && (
                <div className="px-4 pb-4 pt-1 border-t border-slate-100">
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mt-2">Keep ready before you tap</p>
                  <ul className="mt-1.5 space-y-1">
                    {step.keep_ready.map((item, i) => (
                      <li key={i} className="text-xs text-slate-700 flex items-start gap-2">
                        <Circle size={6} className="text-slate-400 mt-1.5 fill-current shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  {step.helper && (
                    <p className="mt-3 text-xs text-sky-700 bg-sky-50 border border-sky-100 rounded-lg px-3 py-2 flex items-start gap-1.5">
                      <Sparkles size={12} className="mt-0.5 shrink-0" />
                      <span>{step.helper}</span>
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => onSetActiveTab?.(step.tab)}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 transition-colors"
                    data-testid={`wizard-cta-${step.key}`}
                  >
                    {step.cta} <ArrowRight size={14} />
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
